import isNil from 'lodash/isNil'

import { withCache } from '@app/utils/cache'
import { createSelector } from '@app/utils/createSelector'
import { createSortHandler } from '@app/utils/createSortHandler'
import moment from '@app/utils/moment'
import { normalize } from '@app/utils/normalizer'

import type { ApiTbankReview, ApiTbankReviewsSummary } from '@app/packages/api/v2/types/entities'
import type { ReviewsPageMeta, TBankReviewRequest } from '@app/packages/api/v2/types/params'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { getServerCache } from '@app/store/actions/contexts/serverCache'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { routingDateSelector, routingUUIDSelector } from './routing'

const summarySlice = createReduxSlice<SliceState<ApiTbankReviewsSummary>>('tbank_reviews_summary')

const reviewsSlice = createReduxSlice<SliceState<Record<string, ApiTbankReview>>>('tbank_reviews')

const previewSlice = createReduxSlice<SliceState<Record<string, ApiTbankReview>>>('tbank_reviews_preview')

const metaSlice = createReduxSlice<Meta>('tbank_reviews_meta')

const setReviewsPageMetaSlice = (meta: ReviewsPageMeta) => metaSlice.update(state => ({ ...state, ...meta, loading: false }))

const setReviewsLoadingSlice = (loading: boolean) => metaSlice.update(state => ({ ...state, loading }))

export const tbankReviewsLoadedSelector = createSelector([metaSlice.selector], meta => {
  return !isNil(meta?.current_page) && !isNil(meta.total_pages) && meta.current_page >= meta.total_pages
})

export const tbankReviewsSummaryResultSelector = createSelector([summarySlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: summarySlice,
      date: { date, expiresInMinutes: 30 },
      load: async dispatch => {
        const [{ getTBankReviewsSummary }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/t_bank_reviews'),
          import('@app/store/actions/api'),
        ])
        const cache = dispatch(getServerCache())
        const resp = await withCache(cache, '5f97d9fa-b4a9-4a59-907f-0c6c8121ad4d tbank_reviews', () => dispatch(callV2Api(getTBankReviewsSummary())))
        return resp.data
      },
    })
  )
)

const tbankReviewsModelsResultSelector = createSelector([reviewsSlice.selector, routingUUIDSelector], (sliceState, uuid) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: reviewsSlice,
      hash: uuid,
      load: async dispatch => {
        const [{ getTBankReviewsByQuery }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/t_bank_reviews'),
          import('@app/store/actions/api'),
        ])
        const resp = await dispatch(callV2Api(getTBankReviewsByQuery({ ...BASIC_REQUEST, page: 1 })))
        dispatch(setReviewsPageMetaSlice(resp.meta))
        return normalize(resp).t_bank_reviews ?? {}
      },
    })
  )
)

export const tbankReviewsListResultSelector = mapThunkSelector(tbankReviewsModelsResultSelector, sortByDate)

/** Landing teaser: its own slice so the hourly cache does not fight the paginated list on /service-reviews. */
const tbankReviewsPreviewModelsResultSelector = createSelector([previewSlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: previewSlice,
      date: { date, expiresInMinutes: 60 },
      load: async dispatch => {
        const [{ getTBankReviewsByQuery }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/t_bank_reviews'),
          import('@app/store/actions/api'),
        ])
        const resp = await dispatch(callV2Api(getTBankReviewsByQuery({ ...BASIC_REQUEST, page: 1, per_page: 3 })))
        return normalize(resp).t_bank_reviews ?? {}
      },
    })
  )
)

export const tbankReviewsPreviewResultSelector = mapThunkSelector(tbankReviewsPreviewModelsResultSelector, sortByDate)

export const fetchNextTbankReviews = () =>
  createThunk(async (dispatch, getState) => {
    const [{ getTBankReviewsByQuery }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/t_bank_reviews'),
      import('@app/store/actions/api'),
    ])
    const meta = metaSlice.selector(getState())
    if (meta?.loading) return
    dispatch(setReviewsLoadingSlice(true))
    try {
      const resp = await dispatch(callV2Api(getTBankReviewsByQuery({ ...BASIC_REQUEST, page: (meta?.current_page ?? 0) + 1 })))
      dispatch(reviewsSlice.update(state => (state ? { ...state, data: { ...state.data, ...normalize(resp).t_bank_reviews } } : state)))
      dispatch(setReviewsPageMetaSlice(resp.meta))
    } catch (e) {
      dispatch(setReviewsLoadingSlice(false))
      throw e
    }
  })

type Meta = { total?: number; total_pages?: number; current_page?: number; loading?: boolean }

function sortByDate(models: Record<string, ApiTbankReview>) {
  return Object.values(models).sort(createSortHandler(r => [moment(r.attributes.created_at).unix()]))
}

const BASIC_REQUEST: TBankReviewRequest = {
  sort: '-created_at',
  per_page: 10,
}
